<template>
  <div :class="['update-score-wrapper', { 'edit-mode': isEditMode }]">

    <!-- Loader -->
    <div v-if="fetchData" class="loading">
      <load-data />
    </div>

    <b-col v-else-if="combinedLabs.length">

      <!-- ACTION BAR -->
      <b-col class="d-flex justify-content-between mb-2">
        <i
          v-if="!isEditMode"
          class="fas fa-edit text-info"
          style="cursor:pointer;font-size:18px"
          @click="enableEdit"
        ></i>

        <div v-if="isEditMode">
          <b-button size="sm" variant="success" @click="saveEdit">
            Save
          </b-button>
          <b-button
            size="sm"
            variant="secondary"
            class="ml-2"
            @click="cancelEdit"
          >
            Cancel
          </b-button>
        </div>
      </b-col>

      <!-- TABLE -->
      <table class="table table-striped table-bordered">

        <!-- HEADER -->
        <thead style="position: sticky; top: 0; background-color: gray;">
          <tr>
            <th rowspan="2">{{ studentListOrder === 'enrollment_no' ? 'Enrollment No.' : studentListOrder === 'admission_number' ? 'Admission No.' : studentListOrder === 'register_number' ? 'Register No.' : 'Roll No.' }}</th>
            <th rowspan="2">Student</th>

            <th
              v-for="exp in labExperiments"
              :key="'exp-' + exp.id"
              :colspan="courseLabs.length"
            >
              {{ exp.no }}. {{ exp.name }}
            </th>

            <th rowspan="2">Avg</th>
          </tr>

          <tr>
            <th
              v-for="item in combinedLabs"
              :key="'lab-' + item.exp.id + '-' + item.lab.id"
              class="text-center"
            >
              <div
                v-for="h in item.headers"
                :key="'h-' + h.id"
                class="mt-1"
              >
                <span>{{ h.name }}:</span>
                <b-form-input
                  type="number"
                  :value="h.max_score"
                  :disabled="!isEditMode"
                  style="width:60px;display:inline-block"
                  @input="updateHeaderScore(item.lab.id, h.id, $event)"
                />
              </div>
            </th>
          </tr>
        </thead>

        <!-- BODY -->
        <tbody>
          <tr
            v-for="student in students"
            :key="student.institution_user_id"
          >
            <td>{{ studentListOrder === 'enrollment_no' ? student.enrollment_no : studentListOrder === 'admission_number' ? student.admission_no : studentListOrder === 'register_number' ? student.register_no : student.roll_number }}</td>
            <td>{{ student.first_name }} {{ student.last_name }}</td>

            <td
              v-for="item in combinedLabs"
              :key="'cell-' + student.institution_user_id + '-' + item.exp.id + '-' + item.lab.id"
            >
              <b-form-input
                v-for="h in item.headers"
                :key="`${student.institution_user_id}-${item.lab.id}-${h.id}`"
                type="number"
                :max="h.max_score"
                :disabled="!isEditMode"
                :value="evaluationTableData[student.institution_user_id][item.exp.id][item.lab.id][h.id]"
                @input="updateScore(student.institution_user_id, item.exp.id, item.lab.id, h.id, $event)"
                @blur="handleScoreEntry(student.institution_user_id, item.exp.id, item.lab.id)"
              />
            </td>

            <td>
              {{ (avarage[student.institution_user_id] || 0).toFixed(2) }}
            </td>
          </tr>
        </tbody>

      </table>

      <b-col class="d-flex justify-content-between mb-2">
        <div v-if="isEditMode">
          <b-button size="sm" variant="success" @click="saveEdit">
            Save
          </b-button>
          <b-button
            size="sm"
            variant="secondary"
            class="ml-2"
            @click="cancelEdit"
          >
            Cancel
          </b-button>
        </div>
      </b-col>
    </b-col>
  </div>
</template>

<script>
import axios from "axios";
import Vue from "vue";

export default {
  props: ["course_id"],

  data() {
    return {
      fetchData: false,
      isEditMode: false,

      students: [],
      studentListOrder: 'roll_number',
      courseLabs: [],
      labExperiments: [],
      headersByActivity: {},

      evaluationTableData: {},
      totalScores: {},
      avarage: {},
    };
  },

  computed: {
    combinedLabs() {
      const result = [];
      this.labExperiments.forEach(exp => {
        this.courseLabs.forEach(lab => {
          result.push({
            exp,
            lab,
            headers: this.headersByActivity[lab.id] || []
          });
        });
      });
      return result;
    }
  },

  async created() {

    try {
      this.fetchData = true;

      await Promise.all([
        this.fetchCourseLabs(),
        this.fetchLabExperiments(),
        this.fetchCourseStudents()
      ]);

      await this.fetchHeaders();
      this.buildTable();
      await this.fetchSavedEvaluationsOptimized();

    } catch (e) {
      console.error(e);
      this.$toast.error("Failed to load data");
    } finally {
      this.fetchData = false;
    }
  },

  methods: {
    enableEdit() {
      this.isEditMode = true;
    },

    cancelEdit() {
      this.isEditMode = false;
      this.$toast.info("Edit cancelled");
    },

    async saveEdit() {
      await this.updateLabEvaluation(false);
      await this.updateLabEvaluationOptionsForAllLabs();
      this.isEditMode = false;
    },

    updateHeaderScore(labId, headerId, value) {
      const headers = this.headersByActivity[labId] || [];
      const header = headers.find(h => h.id === headerId);
      if (header) {
        Vue.set(header, "max_score", Number(value));
      }
    },

    updateScore(studentId, expId, labId, headerId, value) {
      const finalValue =
        value === "" || value === null ? null : Number(value);

      Vue.set(
        this.evaluationTableData[studentId][expId][labId],
        headerId,
        finalValue
      );
    },


    handleScoreEntry(studentId, expId, labId) {
      this.updateTotals(studentId, expId, labId);
    },

    debounce(fn, delay) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    async fetchCourseLabs() {
      const res = await axios.get(
        `${this.$store.getters.getAPIKey.mainAPI}/labactivities?course=${this.course_id}`
      );
      this.courseLabs = res.data.filter(
        l => l.type === "viva_voce" || l.type === "record"
      );
    },

    async fetchLabExperiments() {
      const res = await axios.get(
        `${this.$store.getters.getAPIKey.mainAPI}/labexperiments?course=${this.course_id}`
      );
      this.labExperiments = res.data;
    },

    async fetchHeaders() {
      const res = await axios.get(
        `${this.$store.getters.getAPIKey.mainAPI}/labevaluationheaders?course=${this.course_id}`
      );

      const headers = {};
      this.courseLabs.forEach(lab => {
        headers[lab.id] = res.data
          .filter(h => h.lab_activity_id === lab.id)
          .map(h => ({
            id: h.id,
            name: h.name,
            max_score: h.maximum_mark,
            no: h.no || "1"
          }));
      });

      this.headersByActivity = headers;
    },

    async fetchCourseStudents() {
      const url = this.$store.getters.getAPIKey.createStudent.replace(
        "course_id",
        this.course_id
      )+'?sortBy=lab';
      const response = await this.$axios.get(url);
      this.students = response.data.users ?? response.data[0] ?? [];
      this.studentListOrder = response.data.student_list_order || 'roll_number';
    },

    buildTable() {
      this.students.forEach(s => {
        Vue.set(this.evaluationTableData, s.institution_user_id, {});
        Vue.set(this.totalScores, s.institution_user_id, {});
        Vue.set(this.avarage, s.institution_user_id, 0);

        this.labExperiments.forEach(exp => {
          Vue.set(this.evaluationTableData[s.institution_user_id], exp.id, {});
          Vue.set(this.totalScores[s.institution_user_id], exp.id, {});

          this.courseLabs.forEach(lab => {
            Vue.set(this.evaluationTableData[s.institution_user_id][exp.id], lab.id, {});
            Vue.set(this.totalScores[s.institution_user_id][exp.id], lab.id, 0);

            (this.headersByActivity[lab.id] || []).forEach(h => {
              Vue.set(
                this.evaluationTableData[s.institution_user_id][exp.id][lab.id],
                h.id,
                null
              );
            });
          });
        });
      });
    },

    updateTotals(studentId, expId, labId) {
      let sum = 0;
      Object.values(this.evaluationTableData[studentId][expId][labId]).forEach(v => {
        if (v !== null && v !== "" && !isNaN(v)) {
          sum += Number(v);
        }
      });

      Vue.set(this.totalScores[studentId][expId], labId, sum);

      let total = 0, count = 0;
      Object.values(this.totalScores[studentId]).forEach(exp => {
        Object.values(exp).forEach(v => {
          if (v !== null && !isNaN(v)) {
            total += Number(v);
            count++;
          }
        });
      });

      Vue.set(this.avarage, studentId, count ? total / count : 0);
    },

    async fetchSavedEvaluationsOptimized() {
      const requests = this.courseLabs.map(lab =>
        axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/labevaluations?labActivity=${lab.id}`
        )
      );

      const responses = await Promise.all(requests);

      responses.forEach(res => {
        res.data.evaluations.forEach(e => {
          Vue.set(
            this.evaluationTableData[e.student_id][e.experiment_id][e.lab_activity_id],
            e.header_id,
            Number(e.score)
          );

          this.updateTotals(e.student_id, e.experiment_id, e.lab_activity_id);
        });
      });
    },

    async updateLabEvaluation() {
      const payload = [];
      const labActivityIds = new Set(); //  collect unique IDs

      Object.keys(this.evaluationTableData).forEach(s => {
        Object.keys(this.evaluationTableData[s]).forEach(e => {
          Object.keys(this.evaluationTableData[s][e]).forEach(l => {
            labActivityIds.add(Number(l)); //  store lab_activity_id

            Object.keys(this.evaluationTableData[s][e][l]).forEach(h => {
              const score = this.evaluationTableData[s][e][l][h];

              payload.push({
                student_id: Number(s),
                experiment_id: Number(e),
                header_id: Number(h),
                score: score === "" || score === undefined ? null : score,
                course_id: this.course_id,
                lab_activity_id: Number(l)
              });
            });
          });
        });
      });

      await axios.post(
        `${this.$store.getters.getAPIKey.mainAPI}/labevaluations`,
        {
          lab_activity_ids: Array.from(labActivityIds),
          labEvaluations: payload
        }
      );

      this.$toast.success("Saved successfully");
    },


    async updateLabEvaluationOptionsForAllLabs() {
      for (const lab of this.courseLabs) {
        const payload = (this.headersByActivity[lab.id] || []).map(h => ({
          name: h.name,
          maximum_mark: Number(h.max_score),
          no: h.no,
          lab_activity_id: lab.id
        }));

        await axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/labevaluationheaders`,
          { labHeaders: payload, lab_activity_id: lab.id }
        );
      }
    }
  }
};
</script>

<style scoped>
.update-score-wrapper {
  padding: 15px;
}

.update-score-wrapper.edit-mode {
  position: fixed;
  inset: 0;
  background: #f8f9fa;
  z-index: 1050;
  overflow: auto;
}
</style>
